import React, { useEffect, useState } from "react";
import { database, ref, push, onValue, update, remove } from "./firebase";
import "./BillingSystem.css";
import Navigation from "./Navigation";

export default function EnterprisesSP() {
  /* ================= STATES ================= */
  const [entries, setEntries] = useState([]);

  const [name, setName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

  const [coolJarOut, setCoolJarOut] = useState("");
  const [coolJarIn, setCoolJarIn] = useState("");
  const [coolJarFull, setCoolJarFull] = useState("");

  const [jarOut, setJarOut] = useState("");
  const [jarIn, setJarIn] = useState("");
  const [jarFull, setJarFull] = useState("");

  const [cash, setCash] = useState("");
  const [online, setOnline] = useState("");
  const [balance, setBalance] = useState("");

  const [editId, setEditId] = useState(null);

  /* 🔥 PAYMENT POPUP */
  const [showPopup, setShowPopup] = useState(false);
  const [payId, setPayId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [payDate, setPayDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  /* ================= LOAD HISTORY ================= */
  useEffect(() => {
    onValue(ref(database, "enterprisesSPHistory"), (snap) => {
      if (!snap.exists()) {
        setEntries([]);
        return;
      }
      const list = Object.entries(snap.val()).map(([id, v]) => ({
        id,
        ...v,
        cash: Number(v.cashAmount ?? v.cash ?? 0),
        online: Number(v.onlineAmount ?? v.online ?? 0),
        balance: Number(v.balance ?? 0),
      }));
      list.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEntries(list);
    });
  }, []);

  /* ================= SAVE ================= */
  const handleSave = () => {
    if (!name.trim()) {
      alert("Enter name");
      return;
    }

    const c = Number(cash) || 0;
    const o = Number(online) || 0;
    const b = Number(balance) || 0;
    const total = c + o + b;

    const record = {
      name,
      date,

      coolJarOut: Number(coolJarOut) || 0,
      coolJarIn: Number(coolJarIn) || 0,
      coolJarFull: Number(coolJarFull) || 0,

      jarOut: Number(jarOut) || 0,
      jarIn: Number(jarIn) || 0,
      jarFull: Number(jarFull) || 0,

      /* 🔥 IMPORTANT FOR BANK PAGE */
      cashAmount: c,
      onlineAmount: o,

      /* UI support */
      cash: c,
      online: o,

      balance: b,
      totalAmount: total,
      isPaid: b <= 0,
    };

    if (editId) {
      update(ref(database, `enterprisesSPHistory/${editId}`), record);
    } else {
      push(ref(database, "enterprisesSPHistory"), record);
    }

    resetForm();
  };

  const resetForm = () => {
    setName("");
    setDate(new Date().toISOString().split("T")[0]);
    setCoolJarOut("");
    setCoolJarIn("");
    setCoolJarFull("");
    setJarOut("");
    setJarIn("");
    setJarFull("");
    setCash("");
    setOnline("");
    setBalance("");
    setEditId(null);
  };

  /* ================= EDIT / DELETE ================= */
  const handleEdit = (e) => {
    setEditId(e.id);
    setName(e.name);
    setDate(e.date);
    setCoolJarOut(e.coolJarOut);
    setCoolJarIn(e.coolJarIn);
    setCoolJarFull(e.coolJarFull);
    setJarOut(e.jarOut);
    setJarIn(e.jarIn);
    setJarFull(e.jarFull);
    setCash(e.cash);
    setOnline(e.online);
    setBalance(e.balance);
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete entry?")) {
      remove(ref(database, `enterprisesSPHistory/${id}`));
    }
  };

  /* ================= PAYMENT POPUP ================= */
  const openPayPopup = (id) => {
    setPayId(id);
    setPayAmount("");
    setPayMode("cash");
    setPayDate(new Date().toISOString().split("T")[0]);
    setShowPopup(true);
  };

  const confirmPayment = () => {
    const entry = entries.find((e) => e.id === payId);
    if (!entry) return;

    const amt = Number(payAmount || 0);
    if (amt <= 0 || amt > entry.balance) {
      alert("Invalid amount");
      return;
    }

    const updates = {
      balance: entry.balance - amt,
      cashAmount:
        payMode === "cash" ? entry.cash + amt : entry.cash,
      onlineAmount:
        payMode === "online" ? entry.online + amt : entry.online,
      cash:
        payMode === "cash" ? entry.cash + amt : entry.cash,
      online:
        payMode === "online" ? entry.online + amt : entry.online,
    };

    updates.isPaid = updates.balance <= 0;

    update(ref(database, `enterprisesSPHistory/${payId}`), updates);
    setShowPopup(false);
  };

  /* ================= GROUP BY DATE ================= */
  const grouped = entries.reduce((acc, e) => {
    acc[e.date] = acc[e.date] || [];
    acc[e.date].push(e);
    return acc;
  }, {});

  /* ================= UI ================= */
  return (
    <div className="history-container">
      <Navigation role="special manager" />
      <h2 className="page-title">🏭 Enterprises Entry</h2>

      {/* ENTRY TABLE */}
      <table className="styled-table">
        <tbody>
          <tr><td>Cool Jar Out</td><td><input className="input-small" value={coolJarOut} onChange={e=>setCoolJarOut(e.target.value)} /></td><td /></tr>
          <tr><td>Cool Jar In</td><td><input className="input-small" value={coolJarIn} onChange={e=>setCoolJarIn(e.target.value)} /></td><td><input className="input-small" value={coolJarFull} onChange={e=>setCoolJarFull(e.target.value)} /></td></tr>
          <tr><td>Jar Out</td><td><input className="input-small" value={jarOut} onChange={e=>setJarOut(e.target.value)} /></td><td /></tr>
          <tr><td>Jar In</td><td><input className="input-small" value={jarIn} onChange={e=>setJarIn(e.target.value)} /></td><td><input className="input-small" value={jarFull} onChange={e=>setJarFull(e.target.value)} /></td></tr>
        </tbody>
      </table>

      {/* INPUTS */}
      <div className="entry-controls">
        <input className="input-text" placeholder="Name" value={name} onChange={e=>setName(e.target.value)} />
        <input type="date" className="input-date" value={date} onChange={e=>setDate(e.target.value)} />
        <input className="input-small" placeholder="Cash ₹" value={cash} onChange={e=>setCash(e.target.value)} />
        <input className="input-small" placeholder="Online ₹" value={online} onChange={e=>setOnline(e.target.value)} />
        <input className="input-small" placeholder="Balance ₹" value={balance} onChange={e=>setBalance(e.target.value)} />
        <button className="save-btn" onClick={handleSave}>💾 Save Entry</button>
      </div>

      {/* HISTORY */}
      <h3 className="section-title">📜 Enterprises History</h3>

      {Object.keys(grouped).map(dateKey => (
        <div key={dateKey} className="history-table-section">
          <h4 className="date-header">{dateKey}</h4>
          <table className="styled-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Cool Jar Out</th>
                <th>Cool Jar In</th>
                <th>Jar Out</th>
                <th>Jar In</th>
                <th>Cash</th>
                <th>Online</th>
                <th>Total</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {grouped[dateKey].map(e => (
                <tr key={e.id}>
                  <td><b>{e.name}</b></td>
                  <td>{e.coolJarOut}</td>
                  <td>{e.coolJarIn}{e.coolJarFull>0 && ` (${e.coolJarFull} full)`}</td>
                  <td>{e.jarOut}</td>
                  <td>{e.jarIn}{e.jarFull>0 && ` (${e.jarFull} full)`}</td>
                  <td style={{color:"#0a7"}}>₹{e.cash}</td>
                  <td style={{color:"#06c"}}>₹{e.online}</td>
                  <td><b>₹{e.totalAmount}</b></td>
                  <td style={{color:e.balance<=0?"green":"red"}}>₹{e.balance}</td>
                  <td>{e.balance<=0?"✅ Paid":"⏳ Pending"}</td>
                  <td>
                    <button className="edit-btn" onClick={()=>handleEdit(e)}>✏️ Edit</button>
                    <button className="delete-btn" onClick={()=>handleDelete(e.id)}>🗑️ Delete</button>
                    {e.balance>0 && <button className="paid-btn" onClick={()=>openPayPopup(e.id)}>💰 Amount Paid</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* PAYMENT POPUP */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-card">
            <h3>💰 Amount Paid</h3>
            <input type="date" value={payDate} onChange={e=>setPayDate(e.target.value)} />
            <input placeholder="Amount ₹" value={payAmount} onChange={e=>setPayAmount(e.target.value)} />
            <select value={payMode} onChange={e=>setPayMode(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="online">Online</option>
            </select>
            <div className="popup-actions">
              <button onClick={confirmPayment} className="save-btn">Confirm</button>
              <button onClick={()=>setShowPopup(false)} className="delete-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
